<template>
    <div
        id="itb-login-box"
        class="card itb-top-tail"
        @mouseover="displayWindow"
        @mouseleave="closeWindow"
        v-show="closeButtonUnpressed && (isOpen || showWindowDialog)"
    >
        <button class="itb-mdi-logo itb-close-popup" @click="toggleIcon">
            <img
                src="/images/times-solid.svg"
                alt="close"
                class="itb-icon itb-paperplane"
            />
        </button>

        <div
            v-if="appParams && appParams.staticTranslations"
            class="itb-login-body"
        >
            <h3>
                {{ appParams.staticTranslations["title.client_login_box"] }}
            </h3>
            <form id="loginform" @submit.prevent="validateAndLogin">
                <label
                    id="itb-login-email-label"
                    for="itb-login-form-email"
                    class="itb-cart-mt"
                    :class="{
                        'itb-label-error': $v.client.email.$error,
                    }"
                    >{{ appParams.staticTranslations["general.e_mail"] }}
                </label>
                <div class="input-group">
                    <input
                        type="text"
                        class="form-control"
                        id="itb-login-form-email"
                        :placeholder="
                            appParams.staticTranslations[
                                'general.e_mail_holder'
                            ]
                        "
                        v-model.trim="client.email"
                        @input="$v.client.email.$touch()"
                        :class="{
                            'itb-field-error': $v.client.email.$error,
                        }"
                        ref="email"
                    />
                </div>
                <div v-if="$v.client.email.$dirty">
                    <p
                        class="itb-error-message mb-0"
                        v-if="!$v.client.email.required"
                        aria-live="assertive"
                    >
                        {{ appParams.staticTranslations["general.required"] }}
                    </p>
                    <p
                        class="itb-error-message mb-0"
                        v-if="!$v.client.email.email"
                        aria-live="assertive"
                    >
                        {{
                            appParams.staticTranslations[
                                "general.email_not_valid"
                            ]
                        }}
                    </p>
                </div>
                <label
                    id="itb-login-password-label"
                    for="itb-login-form-password"
                    class="itb-cart-mt"
                    :class="{
                        'itb-label-error': $v.client.password.$error,
                    }"
                    >{{
                        appParams.staticTranslations["general.password"]
                    }}</label
                >
                <div class="input-group">
                    <input
                        type="password"
                        class="form-control"
                        id="itb-login-form-password"
                        :placeholder="
                            appParams.staticTranslations[
                                'general.password_holder'
                            ]
                        "
                        v-model.trim="client.password"
                        @input="$v.client.password.$touch()"
                        :class="{
                            'itb-field-error': $v.client.password.$error,
                        }"
                        ref="password"
                        autocomplete="on"
                    />
                </div>
                <div v-if="$v.client.password.$dirty">
                    <p
                        class="itb-error-message mb-0"
                        v-if="!$v.client.password.required"
                        aria-live="assertive"
                    >
                        {{ appParams.staticTranslations["general.required"] }}
                    </p>
                </div>
                <div class="itb-submit-btn-wrapper">
                    <button
                        type="submit"
                        class="btn btn-primary itb-add-to-basket btn-block"
                        :disabled="submitStatus"
                    >
                        {{
                            appParams.staticTranslations["general.button_login"]
                        }}
                        <span class="mdi mdi-lock-open"></span>
                    </button>
                </div>
            </form>

            <div class="itb-login-details text-center">
                <h5>
                    {{
                        appParams.staticTranslations[
                            "general.forgot_login_details_title"
                        ]
                    }}
                </h5>
                <p>
                    {{
                        appParams.staticTranslations[
                            "general.forgot_login_details_text"
                        ]
                    }}
                </p>
                <h5 class="itb-error-message" v-show="failedLogin">
                    {{
                        appParams.staticTranslations[
                            "general.login_failed_check_info"
                        ]
                    }}
                </h5>
            </div>
        </div>
    </div>
</template>

<script>
import Jwt from "jsonwebtoken";
import { mapActions, mapGetters } from "vuex";
import { required, email } from "vuelidate/lib/validators";

export default {
    name: "ClientLogin",
    props: {
        visible: Boolean,
        tailPosition: Number,
    },
    data() {
        return {
            client: {
                email: "",
                password: "",
                remember_me: false,
            },

            submitStatus: false,
            failedLogin: false,
            showWindowDialog: false,
            closeButtonUnpressed: true,
        };
    },
    computed: {
        ...mapGetters({
            isLoggedIn: "getClientLogged",
            profile: "getClientProfile",
            appParams: "getAppParams",
        }),
        isOpen: {
            get() {
                return this.visible;
            },
            set(value) {
                if (!value) {
                    this.$emit("close");
                }
            },
        },
    },

    validations: {
        client: {
            password: {
                required,
            },
            email: {
                required,
                email,
            },
        },
    },
    methods: {
        ...mapActions(["updateCartCounter"]),

        toggleIcon() {
            this.closeButtonUnpressed = false;
            this.showWindowDialog = false;
            this.$emit("toggleStatic");
            this.$emit("close");
            setTimeout(() => {
                this.closeButtonUnpressed = true;
            }, 500);
        },
        displayWindow() {
            this.showWindowDialog = true;
        },
        closeWindow() {
            this.showWindowDialog = false;
        },
        reset() {
            this.$v.$reset();
        },
        close() {
            this.toggleIcon();
            this.reset();
        },
        dialogState(type, state = true) {
            this.dialog[type] = state;
        },

        async validateAndLogin() {
            this.$v.$touch();
            if (this.$v.$invalid) {
                this.submitStatus = false;
                for (let key in Object.keys(this.$v)) {
                    const input = Object.keys(this.$v)[key];
                    if (input.includes("$")) return false;
                    if (this.$v[input].$error) {
                        this.$refs[input].scrollIntoView({
                            behavior: "smooth",
                            block: "center",
                        });
                        break;
                    }
                }
            } else {
                this.submitStatus = true;
                await axios
                    .post("/api/auth/client-login", this.client)
                    .then((response) => {
                        if (response.data.status_code != 403) {
                            localStorage.setItem(
                                "itb-spa-token-client",
                                Jwt.sign(
                                    { client: response.data },
                                    "itbjwttoken-client"
                                )
                            );

                            this.$store.dispatch(
                                "clientAuthenticate",
                                response.data.client
                            );
                            this.close();
                            this.loadCartCounter();
                            this.$emit("closeNavbar");
                            this.$eventBus.$emit("refreshByLogin");
                            this.toggleIcon();

                            if (this.$route.path == "/choose-the-graphic") {
                                this.$router.push("/choose-the-graphic/2D");
                            }

                            setTimeout(() => {
                                this.submitStatus = false;
                                this.client.email = "";
                                this.client.password = "";
                            }, 500);
                        } else {
                            this.failedLogin = true;
                            setTimeout(() => {
                                this.failedLogin = false;
                            }, 1500);
                        }
                    })
                    .catch((error) => {
                        this.submitStatus = false;
                        this.failedLogin = true;
                        setTimeout(() => {
                            this.failedLogin = false;
                        }, 1500);
                    });
            }
        },
        loadCartCounter() {
            axios
                .post("/loadCartCounter", this.profile)
                .then((response) => {
                    this.updateCartCounter(response.data);
                })
                .catch((error) => {
                    console.log(error);
                });
        },
    },
};
</script>
